import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Divider,
  Alert,
  Link
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import { useNavigate } from 'react-router-dom';
import { isRTL } from '../i18n';
import { isVirtualPortfolioEnabled } from '../constants/featureFlags';

const AboutPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  // Check if current language is RTL
  const rtl = isRTL();

  const features = [
    {
      icon: '📊',
      title: 'Real-Time Portfolio Tracking',
      description: 'Monitor your crypto holdings with live price updates, profit/loss calculations, and comprehensive portfolio analytics.'
    },
    {
      icon: '🔗',
      title: 'Multi-Wallet Support',
      description: 'Connect multiple wallets and exchanges to get a unified view of all your cryptocurrency assets in one place.'
    },
    ...(isVirtualPortfolioEnabled() ? [{
      icon: '🎮',
      title: 'Virtual Portfolio',
      description: 'Practice trading strategies risk-free with our virtual portfolio feature. Learn and experiment without risking real money.'
    }] : []),
    {
      icon: '📈',
      title: 'Exit Strategies',
      description: 'Set up automated exit strategies with stop-loss and take-profit orders to protect your investments and maximize gains.'
    },
    {
      icon: '📱',
      title: 'Cross-Platform Access',
      description: 'Access your portfolio from any device - desktop, tablet, or mobile. Your data syncs seamlessly across all platforms.'
    },
    {
      icon: '🌍',
      title: 'Multi-Currency Support',
      description: 'View your portfolio in multiple fiat currencies and track performance in your preferred currency.'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Back Button */}
      <Box sx={{ mb: 3 }}>
        <IconButton 
          onClick={() => navigate(-1)} 
          sx={{ color: 'primary.main' }}
          aria-label="go back"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body2" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
          {t('about.back')}
        </Typography>
      </Box>

      {/* Header */}
      <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
        {t('about.title')}
      </Typography>

      {/* Mission */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
        {t('about.ourMission')}
      </Typography>
      <Typography variant="body1" paragraph>
        {t('about.missionText1')}
      </Typography>
      <Typography variant="body1" paragraph sx={{ mb: 4 }}>
        {t('about.missionText2')}
      </Typography>

      {/* Features */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
        {t('about.keyFeatures')}
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card sx={{ 
              height: '100%', 
              borderLeft: rtl ? 'none' : '4px solid',
              borderRight: rtl ? '4px solid' : 'none',
              borderColor: 'primary.main'
            }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{feature.icon}</span>
                  {t(`about.features.${index}.title`)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(`about.features.${index}.description`)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Security & Privacy */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: 'primary.50' }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
          {t('about.securityAndPrivacy')}
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('about.readOnlyAccess')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('about.readOnlyText')}
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('about.noSensitiveData')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('about.noSensitiveText')}
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('about.dataProtection')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('about.dataProtectionText')}
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('about.privacyCommitment')}
        </Typography>
        <Typography variant="body1">
          {t('about.privacyCommitmentText')}
        </Typography>
      </Paper>

      {/* Virtual Portfolio Privacy */}
      {isVirtualPortfolioEnabled() && (
        <Paper sx={{ 
          p: 3, 
          mb: 4, 
          bgcolor: 'success.50', 
          borderLeft: rtl ? 'none' : '4px solid',
          borderRight: rtl ? '4px solid' : 'none',
          borderColor: 'success.main' 
        }}>
          <Typography variant="h5" gutterBottom sx={{ color: 'success.main', mb: 2 }}>
            {t('about.virtualPortfolioPrivacy')}
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {t('about.virtualPortfolioFeatures')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('about.virtualPortfolioFeaturesText')}
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {t('about.publicVisibility')}
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" variant="body1">
              {t('about.virtualTransactions')}
            </Typography>
            <Typography component="li" variant="body1">
              {t('about.publicName')}
            </Typography>
            <Typography component="li" variant="body1">
              {t('about.leaderboards')}
            </Typography>
          </Box>
          
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {t('about.realPortfolioPrivacy')}
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            <Typography component="li" variant="body1">
              {t('about.completePrivacy')}
            </Typography>
            <Typography component="li" variant="body1">
              {t('about.noSharing')}
            </Typography>
            <Typography component="li" variant="body1">
              {t('about.separateSystems')}
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>{t('about.virtualPortfolioConsent')}</strong>
          </Alert>
        </Paper>
      )}

      {/* Technology */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
        {t('about.technology')}
      </Typography>
      <Typography variant="body1" paragraph>
        {t('about.technologyText')}
      </Typography>
      <Box component="ul" sx={{ pl: 2, mb: 4 }}>
        <Typography component="li" variant="body1">
          {t('about.realTimeData')}
        </Typography>
        <Typography component="li" variant="body1">
          {t('about.responsiveDesign')}
        </Typography>
        <Typography component="li" variant="body1">
          {t('about.secureInfrastructure')}
        </Typography>
        <Typography component="li" variant="body1">
          {t('about.fastPerformance')}
        </Typography>
        <Typography component="li" variant="body1">
          {t('about.regularUpdates')}
        </Typography>
      </Box>

      {/* Contact & Support */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: 'grey.50' }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
          {t('about.contactSupport')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('about.contactSupportText')}
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('about.emailSupport')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('about.emailSupportText')}
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('about.featureRequests')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('about.featureRequestsText')}
        </Typography>
        
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {t('about.bugReports')}
        </Typography>
        <Typography variant="body1">
          {t('about.bugReportsText')}
        </Typography>
      </Paper>

      {/* Legal Disclaimers */}
      <Alert severity="warning" sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {t('about.legalDisclaimers')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('about.legalDisclaimersText')}
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 0 }}>
          <Typography component="li" variant="body1">
            {t('about.legalRisk')}
          </Typography>
          <Typography component="li" variant="body1">
            {t('about.legalPastPerformance')}
          </Typography>
          <Typography component="li" variant="body1">
            {t('about.legalResearch')}
          </Typography>
          <Typography component="li" variant="body1">
            {t('about.legalAdvisor')}
          </Typography>
          {isVirtualPortfolioEnabled() && (
            <Typography component="li" variant="body1">
              {t('about.legalVirtualPortfolio')}
            </Typography>
          )}
        </Box>
      </Alert>

      <Divider sx={{ my: 3 }} />
      <Typography variant="body2" color="text.secondary" align="center">
        {t('about.copyright', { year: new Date().getFullYear() })}
      </Typography>
    </Container>
  );
};

export default AboutPage; 